// Home Stock location results list (items + empty categories), shared by review detail and wizard preview.

import { type ReactElement } from 'react';
import { type InventoryLevel } from '@fdh/shared';
import { useLanguage } from '../../contexts/LanguageContext';
import { inventoryStockLevelUiLabel } from '../../lib/inventoryStockLevelUiLabel';
import { resolveInventoryV2ExpiryBadge } from '../../lib/inventoryV2ExpiryBadge';
import { inventoryV2ExpiryBadgeNeedsDateHint } from '../../lib/inventoryV2ExpiryHint';
import { buildInventoryPersistedPhotoApiPath, type InventoryPersistedPhotoRef } from '../../lib/inventoryPersistedPhotoApi';
import { InventoryCategoryBadge } from './InventoryCategoryBadge';
import { HomeStockLevelIcon } from './HomeStockLevelIcon';
import { HomeStockLevelLegend } from './HomeStockLevelLegend';
import { StocktakeUploadedPhotoThumb } from './StocktakeUploadedPhotoThumb';
import { PressableHint } from '../ui/PressableHint';
import { PressableHintProvider } from '../ui/PressableHintContext';

const EXPIRY_YMD_REGEX = /^\d{4}-\d{2}-\d{2}$/;

const INVENTORY_V2_LONG_ITEM_NAME_CHAR_THRESHOLD = 40;

const INVENTORY_V2_ITEM_NAME_CATEGORY_BADGE_PR_CLASS =
  'max-md:pr-2 md:pr-[min(6.5rem,max(0px,calc(min(148px,46vw)-2.75rem)))]';

export type HomeStockLocationResultItem = {
  itemKey: string;
  itemName: string;
  categoryName: string;
  sortOrder: number;
  stockLevel: InventoryLevel;
  expiries: Array<{ expiryDate: string; isNoExpiry?: boolean }>;
  photoDisplayUrl?: string;
  persistedPhotoRef?: InventoryPersistedPhotoRef;
};

type HomeStockLocationResultsSectionProps = {
  items: HomeStockLocationResultItem[];
  emptyCategories: string[];
  categoryBadgeIndexMap: ReadonlyMap<string, number>;
  /** Optional display label resolver (e.g. task category translation). */
  resolveCategoryLabel?: (category: string) => string;
  todayYmd: string;
  emptyMessage: string;
  showHeading?: boolean;
  /** Required when items include `persistedPhotoRef` for submission photos. */
  taskInstanceId?: string;
};

export const HomeStockLocationResultsSection = ({
  items,
  emptyCategories,
  categoryBadgeIndexMap,
  resolveCategoryLabel,
  todayYmd,
  emptyMessage,
  showHeading = true,
  taskInstanceId,
}: HomeStockLocationResultsSectionProps): ReactElement => {
  const { t } = useLanguage();

  return (
    <div className="flex flex-col gap-4">
      {showHeading ? (
        <div className="flex items-center justify-between gap-3 px-1">
          <h3 className="text-base font-bold text-gray-900 dark:text-gray-50">
            {t('task.review.stocktakeResultsHeading')}
          </h3>
          <HomeStockLevelLegend />
        </div>
      ) : null}
      {items.length === 0 && emptyCategories.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-gray-200 bg-white px-4 py-8 text-center text-sm text-gray-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400">
          {emptyMessage}
        </p>
      ) : (
        <PressableHintProvider>
          <div className="flex flex-col gap-3">
            {items.map((item, itemIndex) => {
              const stockLevelAriaLabel = inventoryStockLevelUiLabel(item.stockLevel, t);
              const categoryTrimmed = item.categoryName.trim();
              const categoryDisplayLabel = resolveCategoryLabel
                ? resolveCategoryLabel(categoryTrimmed)
                : categoryTrimmed;
              const photoDisplayUrl = (item.photoDisplayUrl ?? '').trim();
              const persistedPhotoApiPath =
                taskInstanceId && item.persistedPhotoRef
                  ? buildInventoryPersistedPhotoApiPath(taskInstanceId, item.persistedPhotoRef)
                  : undefined;
              const showItemPhoto = photoDisplayUrl !== '' || persistedPhotoApiPath !== undefined;
              const isLongInventoryItemName =
                item.itemName.length > INVENTORY_V2_LONG_ITEM_NAME_CHAR_THRESHOLD;
              return (
                <div
                  key={item.itemKey}
                  className="relative overflow-visible rounded-2xl border border-gray-200/80 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900"
                >
                  {categoryTrimmed !== '' ? (
                    <InventoryCategoryBadge
                      category={categoryTrimmed}
                      displayCategory={categoryDisplayLabel}
                      badgeIndexMap={categoryBadgeIndexMap}
                      variant="detail"
                      hintId={`preview-cat-${itemIndex}`}
                    />
                  ) : null}
                  <div className="flex items-stretch gap-3 px-3 py-2.5">
                    {showItemPhoto ? (
                      <div className="h-[4.5rem] w-[4.5rem] shrink-0 overflow-hidden rounded-xl border border-gray-200/80 dark:border-gray-600">
                        <StocktakeUploadedPhotoThumb
                          displayUrl={photoDisplayUrl}
                          authedApiPath={persistedPhotoApiPath}
                          imageAlt={t('task.review.inventoryItemPhotoAria')}
                          enlargeAriaLabel={t('inventory.enlargePhotoAria')}
                          closeViewerLabel={t('common.closeViewer')}
                          removeAriaLabel=""
                          onRequestRemove={() => undefined}
                          actionsDisabled
                          thumbClassName="h-[4.5rem] w-[4.5rem]"
                        />
                      </div>
                    ) : null}
                    <div className="flex min-h-[4.5rem] min-w-0 flex-1 flex-col justify-center gap-1.5">
                      <p
                        title={item.itemName}
                        className={[
                          'font-bold text-gray-900 dark:text-gray-50',
                          'max-md:line-clamp-none md:line-clamp-3',
                          isLongInventoryItemName
                            ? 'text-sm leading-snug'
                            : 'text-base leading-tight',
                          categoryTrimmed !== '' ? INVENTORY_V2_ITEM_NAME_CATEGORY_BADGE_PR_CLASS : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                      >
                        {item.itemName}
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {(item.expiries ?? []).map((e, expIdx) => {
                          if (e.isNoExpiry === true || e.expiryDate === 'NO_EXPIRY') {
                            return (
                              <span
                                key={`no-exp-${expIdx}`}
                                className="inline-flex items-center gap-1 rounded-lg border border-gray-200/80 bg-[#F7F9FC] px-2 py-1 text-[10px] font-medium text-gray-600 dark:border-gray-600 dark:bg-gray-800/80 dark:text-gray-300"
                              >
                                <svg className="h-2.5 w-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                  />
                                </svg>
                                {t('inventory.expiry.noExpiryLabel')}
                              </span>
                            );
                          }
                          const d = e.expiryDate.trim();
                          if (!EXPIRY_YMD_REGEX.test(d)) {
                            return null;
                          }
                          const { label: expiryLabel, badgeClassName: expiryBadgeClass } = resolveInventoryV2ExpiryBadge(
                            t,
                            todayYmd,
                            d,
                          );
                          const expiryBadgeIcon = (
                            <svg className="h-2.5 w-2.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                              />
                            </svg>
                          );
                          const expiryBadgeBody = (
                            <>
                              {expiryBadgeIcon}
                              {expiryLabel}
                            </>
                          );
                          const expiryBadgeClassName = `inline-flex items-center gap-1 rounded-lg border px-2 py-1 text-[10px] font-medium ${expiryBadgeClass}`;
                          const showExpiryDateHint = inventoryV2ExpiryBadgeNeedsDateHint(todayYmd, d);
                          if (showExpiryDateHint) {
                            return (
                              <PressableHint
                                key={`${d}-${expIdx}`}
                                hintId={`preview-exp-${itemIndex}-${expIdx}`}
                                detail={d}
                                ariaLabel={`${d}, ${expiryLabel}`}
                                placement="top"
                                triggerClassName={expiryBadgeClassName}
                              >
                                {expiryBadgeBody}
                              </PressableHint>
                            );
                          }
                          return (
                            <span
                              key={`${d}-${expIdx}`}
                              aria-label={`${d}, ${expiryLabel}`}
                              className={expiryBadgeClassName}
                            >
                              {expiryBadgeBody}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                    <div
                      className={
                        categoryTrimmed !== ''
                          ? 'mt-4 flex shrink-0 items-center self-start'
                          : 'flex shrink-0 items-center self-center'
                      }
                    >
                      <HomeStockLevelIcon
                        level={item.stockLevel}
                        size="lg"
                        hintId={`preview-jar-${itemIndex}`}
                        tooltipLabel={stockLevelAriaLabel}
                        blinkWhenEmpty
                      />
                    </div>
                  </div>
                </div>
              );
            })}
            {emptyCategories.map((category) => {
              const categoryDisplayLabel = resolveCategoryLabel
                ? resolveCategoryLabel(category)
                : category;
              return (
              <div
                key={`empty-cat-${category}`}
                className="rounded-2xl border border-amber-200/90 bg-amber-50/90 p-4 shadow-sm dark:border-amber-800 dark:bg-amber-950/35"
              >
                <div className="flex gap-3">
                  <div
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-200/80 text-amber-800 dark:bg-amber-900/70 dark:text-amber-200"
                    aria-hidden
                  >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                      />
                    </svg>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[15px] font-bold text-amber-950 dark:text-amber-50">{categoryDisplayLabel}</p>
                    <p className="mt-1 text-sm leading-relaxed text-amber-900/90 dark:text-amber-100/90">
                      {t('task.review.inventoryCategoryEmptyAtLocationBody')}
                    </p>
                  </div>
                </div>
              </div>
            );
            })}
          </div>
        </PressableHintProvider>
      )}
    </div>
  );
};
